/**
 * The primitives.
 *
 * Three rules run through all of them, from ux.md §2, and each is enforced here rather than left to
 * whoever assembles a screen:
 *
 * 1. **Targets are read from the density**, never hard-coded. `min-h-touch` resolves to 56 px in
 *    the arena and 44 px at a desk, so a component cannot render desk-sized controls into an arena
 *    by being used in the wrong place.
 * 2. **Nothing carries meaning by colour alone.** Every tone that means something ships with a
 *    word beside it, and the components make the word the required argument rather than the
 *    optional one.
 * 3. **Destructive is slow, everything else is fast.** `Button` has no "danger" variant that is
 *    merely red — a destructive action goes through `ConfirmDialog`, which requires a reason.
 */

import {
  forwardRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
  useRef,
  useState,
} from "react";
import { cn } from "./cn.js";

/* ------------------------------------------------------------------ button */

export type ButtonVariant = "primary" | "secondary" | "quiet" | "destructive";
export type ButtonSize = "default" | "large";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "raised border-brand bg-brand text-brand-contrast shadow-lg hover:bg-[color-mix(in_srgb,var(--pw-brand-primary)_88%,white)]",
  secondary: "border-line bg-paper text-ink shadow-sm hover:border-line-strong hover:bg-sunk",
  quiet: "border-transparent bg-transparent text-ink hover:bg-sunk/80",
  // Only reached through a confirmation flow. The colour is the last signal, not the first.
  destructive: "border-danger bg-paper text-danger shadow-sm hover:bg-danger-surface",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  /** Shown in place of the label while an action is in flight. Never blocks the whole screen. */
  busy?: boolean | undefined;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "default", busy, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={rest.type ?? "button"}
      disabled={disabled ?? busy}
      aria-busy={busy || undefined}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-md border font-semibold",
        "min-h-touch tracking-normal transition disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-55 active:scale-[0.98]",
        size === "large" ? "px-8 text-body-large" : "px-5",
        BUTTON_VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

/* ------------------------------------------------------------------ surface */

export function Card({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>): ReactNode {
  return (
    <div
      data-card=""
      className={cn("rounded-lg border border-line bg-paper p-5 shadow-sm", className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Section({
  title,
  description,
  actions,
  children,
  className,
}: {
  title: string;
  description?: ReactNode | undefined;
  actions?: ReactNode | undefined;
  children: ReactNode;
  className?: string | undefined;
}): ReactNode {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className={cn("mb-8", className)}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={headingId} className="text-title font-bold tracking-normal">
            {title}
          </h2>
          {/*
           * The one description on a screen that keeps a measure of its own.
           *
           * A paragraph inside a card runs to the card's width, because text stopping short of a
           * border the reader can see does not read as a comfortable measure, it reads as a layout
           * fault — which is why the caps came off those. This one sits above the cards with no
           * edge to stop short of, so `68ch` is what it is meant to be: the measure for a
           * standalone line of prose.
           */}
          {description && <p className="mt-1 max-w-[68ch] text-muted">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap gap-gap">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

/* -------------------------------------------------------------------- tone */

export type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "pending";

const TONES: Record<Tone, string> = {
  neutral: "bg-sunk text-muted",
  accent: "bg-accent-surface text-accent",
  success: "bg-success-surface text-success",
  warning: "bg-warning-surface text-warning",
  danger: "bg-danger-surface text-danger",
  pending: "bg-pending-surface text-pending",
};

/**
 * A short status word.
 *
 * `children` is required and is the status itself — there is no icon-only form, because a bare
 * coloured dot is the first thing lost to sunlight and the first thing lost to colour blindness.
 */
export function Badge({
  tone = "neutral",
  className,
  children,
}: {
  tone?: Tone | undefined;
  className?: string | undefined;
  children: ReactNode;
}): ReactNode {
  return (
    <span
      className={cn(
        /*
         * `max-w-full` rather than `whitespace-nowrap`.
         *
         * A badge is a word or two and will sit on one line without being told to; what `nowrap`
         * actually did was let a long one run out past the edge of whatever held it — a contrast
         * verdict escaped its card by about a third of its own width. Wrapping inside the box is
         * the better failure: it stays readable, and it is visible to whoever wrote the string
         * that it is too long to be a badge.
         */
        "inline-flex max-w-full items-center rounded-full px-2.5 py-1 text-caption font-bold ring-1 ring-current/10",
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * A message about the state of things.
 *
 * `label` is the severity in words — *Must fix*, *Check*, *Not yet payable*. It is a required
 * prop, and it is what a screen reader announces and what a person reads in the sun.
 */
export function Callout({
  tone = "accent",
  label,
  children,
  actions,
  className,
}: {
  tone?: Tone | undefined;
  label: string;
  children: ReactNode;
  actions?: ReactNode | undefined;
  className?: string | undefined;
}): ReactNode {
  return (
    <div
      role={tone === "danger" || tone === "warning" ? "alert" : "status"}
      className={cn(
        "flex gap-3 rounded-lg border border-current/15 p-4 shadow-sm",
        TONES[tone],
        className,
      )}
    >
      <span className="pt-0.5 text-caption font-bold tracking-wide uppercase whitespace-nowrap">
        {label}
      </span>
      <div className="min-w-0 flex-1">
        {children}
        {actions && <div className="mt-2 flex flex-wrap gap-gap">{actions}</div>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- fields */

interface FieldShellProps {
  label: string;
  hint?: ReactNode | undefined;
  /** Errors are placed next to the control, never gathered into a summary (ux.md §3.2). */
  error?: string | undefined;
  required?: boolean | undefined;
  /**
   * Whether to mark the field "optional".
   *
   * On by default, because a form should say which questions may be skipped rather than making
   * someone find out by submitting. Off for controls that are not questions at all — a search box
   * is a tool, and calling it optional is just noise beside the thing someone came to use.
   */
  showOptional?: boolean | undefined;
  /**
   * Hide the label visually, keeping it for assistive technology.
   *
   * Only for a control whose name is already unambiguous on screen — a cell in a table with a
   * column header and a row header, an entry in a list that names itself. Never as a way to make a
   * form shorter: a label a sighted person has to infer is a label that will be inferred wrongly.
   */
  labelHidden?: boolean | undefined;
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  className?: string | undefined;
}

function FieldShell({
  label,
  hint,
  error,
  required,
  showOptional = true,
  labelHidden,
  children,
  className,
}: FieldShellProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ");

  return (
    // Four rows — label, hint, control, error — which a `FieldGrid` turns into a subgrid so that a
    // row of fields shares them. Without it, a field with help text pushes its own control down and
    // the one beside it does not, and a form reads as though it were assembled by accident.
    //
    // The subgrid is applied by `[data-field-band] > [data-field]` in the theme rather than by
    // classes here, because it is only correct inside a `FieldGrid`. Any *other* grid parent — and
    // half the screens put a filter row in a plain `grid gap-3 sm:grid-cols-2` — would apply its own
    // row gap between all four rows, opening a 36 px hole between every label and its control. That
    // was the actual bug: not a missing style, a style that leaked into parents it was never meant
    // for. Outside a band this is an ordinary block, which stacks the four in the same order.
    <div data-field="" className={cn("mb-4", className)}>
      {/* The label row is claimed by a wrapper, not by the label itself. `sr-only` positions the
          label absolutely, and an absolutely positioned child is not a grid item — so a hidden
          label silently vacated row one and every row below it moved up, landing the control in
          the help row. That is only visible when a field beside it has a *visible* label: the two
          then sit a whole row apart, which is the misalignment a search box beside a select always
          had. The wrapper stays in flow and keeps the row whether or not the label is shown; it
          collapses to nothing when hidden, so a band of hidden labels is still tight. */}
      <div>
        <label htmlFor={id} className={cn("mb-1 block font-semibold", labelHidden && "sr-only")}>
          {label}
          {required && (
            <span className="ml-1 text-danger" aria-hidden="true">
              *
            </span>
          )}
          {!required && showOptional && (
            <span className="ml-2 font-normal text-muted">optional</span>
          )}
        </label>
      </div>
      {/* Rendered even when empty. The row has to be claimed either way, or a field with no help
          text would put its control in the help row and defeat the alignment. An empty row costs
          nothing: it collapses to zero height unless some field in the same band fills it. */}
      {hint ? (
        <p id={hintId} className="mb-1 text-caption text-muted">
          {hint}
        </p>
      ) : (
        <span aria-hidden="true" className="block h-0" />
      )}
      {children({ id, describedBy: describedBy || undefined, invalid: Boolean(error) })}
      {error ? (
        <p id={errorId} className="mt-1 font-semibold text-danger">
          {error}
        </p>
      ) : (
        <span aria-hidden="true" className="block h-0" />
      )}
    </div>
  );
}

const CONTROL =
  "w-full min-h-touch rounded-md border bg-paper/95 px-3 text-ink shadow-inner shadow-black/[0.03] transition " +
  "focus:border-brand aria-[invalid=true]:border-danger aria-[invalid=true]:border-2";

export interface TextFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "id" | "aria-describedby"
> {
  label: string;
  hint?: ReactNode | undefined;
  error?: string | undefined;
  showOptional?: boolean | undefined;
  labelHidden?: boolean | undefined;
  /** Classes for the field's wrapper, where `className` styles the control itself. */
  fieldClassName?: string | undefined;
  /**
   * A control that belongs *inside* the box — a colour swatch, a unit, a clear button.
   *
   * Inside rather than beside, and that is the whole reason this exists. A second control set next
   * to the field is a second element in the row, and `FieldShell` claims four grid rows so that a
   * band of fields shares one line for labels and one for controls; anything wrapped around it to
   * hold a sibling breaks the `[data-field-band] > [data-field]` relationship the subgrid depends
   * on. The symptom is a field whose label wraps onto two lines sitting a whole row below the one
   * beside it — which is precisely the thing that alignment machinery was written to prevent.
   *
   * The input gets trailing padding to match, so text never runs underneath it.
   */
  adornment?: ReactNode | undefined;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    label,
    hint,
    error,
    required,
    showOptional,
    labelHidden,
    fieldClassName,
    className,
    adornment,
    ...rest
  },
  ref,
) {
  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      required={required}
      showOptional={showOptional}
      labelHidden={labelHidden}
      className={fieldClassName}
    >
      {({ id, describedBy, invalid }) => {
        const control = (
          <input
            ref={ref}
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            required={required}
            className={cn(CONTROL, "border-line-strong", adornment && "pr-14", className)}
            {...rest}
          />
        );
        // One element in the control row either way, so the row keeps its place in the subgrid.
        return adornment ? (
          <div className="relative">
            {control}
            <span className="absolute inset-y-0 right-2 flex items-center">{adornment}</span>
          </div>
        ) : (
          control
        );
      }}
    </FieldShell>
  );
});

export interface TextAreaFieldProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "id" | "aria-describedby"
> {
  label: string;
  hint?: ReactNode | undefined;
  error?: string | undefined;
  labelHidden?: boolean | undefined;
  fieldClassName?: string | undefined;
}

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  function TextAreaField(
    { label, hint, error, required, labelHidden, fieldClassName, className, ...rest },
    ref,
  ) {
    return (
      <FieldShell
        label={label}
        hint={hint}
        error={error}
        required={required}
        labelHidden={labelHidden}
        className={fieldClassName}
      >
        {({ id, describedBy, invalid }) => (
          <textarea
            ref={ref}
            id={id}
            rows={rest.rows ?? 3}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            required={required}
            className={cn(CONTROL, "border-line-strong py-2", className)}
            {...rest}
          />
        )}
      </FieldShell>
    );
  },
);

export interface SelectFieldProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "id" | "aria-describedby"
> {
  label: string;
  hint?: ReactNode | undefined;
  error?: string | undefined;
  showOptional?: boolean | undefined;
  labelHidden?: boolean | undefined;
  fieldClassName?: string | undefined;
  options: ReadonlyArray<{ value: string; label: string }>;
  placeholder?: string | undefined;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  {
    label,
    hint,
    error,
    required,
    showOptional,
    labelHidden,
    fieldClassName,
    options,
    placeholder,
    className,
    ...rest
  },
  ref,
) {
  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      required={required}
      showOptional={showOptional}
      labelHidden={labelHidden}
      className={fieldClassName}
    >
      {({ id, describedBy, invalid }) => (
        <select
          ref={ref}
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          required={required}
          className={cn(CONTROL, "border-line-strong", className)}
          {...rest}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
});

/* ---------------------------------------------------------------- combo box */

/**
 * Text as it should be compared, not as it is written.
 *
 * Case- and accent-insensitive, so "kitigan" finds "Kitigan Zibi" and "kahnawa" finds "Kahnawà:ke";
 * and without apostrophes, so "womens" finds "Women's" — the apostrophe is the one character a
 * person searching on an arena tablet reliably leaves out.
 *
 * Exported because every search box in the product should agree on what "the same word" means: the
 * community combo box below, and the desk's filter over a long list of categories.
 */
export function foldText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/['’]/g, "")
    .toLowerCase();
}

const fold = foldText;

export interface ComboBoxRow {
  text: string;
  /** False for the keep-what-I-typed row, which is styled and read differently. */
  listed: boolean;
}

/**
 * What a combo box offers, given its options and what has been typed so far.
 *
 * Pulled out of the component because this is the part with rules in it, and rules belong somewhere
 * they can be asserted rather than clicked: substring matching that ignores case and accents,
 * a keep-what-I-typed row that appears only when it would add something, and a cap that is *counted*
 * rather than silently dropped.
 */
export function comboBoxRows(
  options: readonly string[],
  typed: string,
  maxVisible = 50,
): { rows: ComboBoxRow[]; hidden: number } {
  const trimmed = typed.trim();
  const needle = fold(trimmed);
  const matches = needle ? options.filter((option) => fold(option).includes(needle)) : options;
  const visible = matches.slice(0, maxVisible);
  // Pointless when what was typed is already one of the options, and there is nothing to keep when
  // nothing has been typed.
  const offerTyped = trimmed.length > 0 && !options.some((option) => fold(option) === needle);

  return {
    rows: [
      ...visible.map((option) => ({ text: option, listed: true })),
      ...(offerTyped ? [{ text: trimmed, listed: false }] : []),
    ],
    hidden: matches.length - visible.length,
  };
}

export interface ComboBoxFieldProps {
  label: string;
  hint?: ReactNode | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
  showOptional?: boolean | undefined;
  fieldClassName?: string | undefined;
  /** The current text. Always a plain string — what is stored is what is shown. */
  value: string;
  onChange: (value: string) => void;
  onBlur?: (() => void) | undefined;
  /** The offered answers, in the order they should be read. Filtered as the person types. */
  options: readonly string[];
  placeholder?: string | undefined;
  /** Beyond this many matches the list is cut and says so, rather than rendering a wall. */
  maxVisible?: number | undefined;
}

/**
 * A list you can search, that still accepts an answer the list has never heard of.
 *
 * Built for the home-community field, which is the field this shape exists for: the platform knows
 * a directory of communities, and that directory **is not the set of places a dancer can be from**
 * (D46). So the list has to be a shortcut past typing and never a constraint on what may be typed —
 * a `<select>` would be wrong here in a way that is invisible until the person in front of the desk
 * comes from somewhere not on it, at which point a registrar is stuck.
 *
 * Deliberately not the `<datalist>` the public page uses. That page has to work with no JavaScript
 * on venue wifi, so a native control that degrades to a plain text input is exactly right there.
 * The desk is a React app on a tablet outdoors with a queue (ux.md §3.3), and a datalist gives it
 * no visible list until you type, no targets sized by the density, and no way to *say* that what
 * you typed is being kept. Here the list is on screen, every row is a `min-h-touch` target, and the
 * last row spells out the escape hatch — "Use “Rocky Bay” — not in the list" — so choosing something
 * of your own is an offered option rather than a thing you have to know is allowed.
 *
 * There is no "Other" mode to switch into, on purpose: a mode is a thing to be stuck in, and the
 * typing that would select it is already the typing that enters the answer.
 */
export function ComboBoxField({
  label,
  hint,
  error,
  required,
  showOptional,
  fieldClassName,
  value,
  onChange,
  onBlur,
  options,
  placeholder,
  maxVisible = 50,
}: ComboBoxFieldProps): ReactNode {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const listId = useId();
  // Set while a pointer is down inside the list, so the blur that a click causes does not close the
  // list before the click lands on the row that caused it.
  const clicking = useRef(false);

  const { rows, hidden } = comboBoxRows(options, value, maxVisible);

  function choose(index: number) {
    const row = rows[index];
    if (!row) return;
    onChange(row.text);
    setOpen(false);
    setActive(-1);
    onBlur?.();
  }

  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      required={required}
      showOptional={showOptional}
      className={fieldClassName}
    >
      {({ id, describedBy, invalid }) => (
        <div
          className="relative"
          onBlur={(event) => {
            // Only when focus has actually left the whole control — moving between the input and a
            // row inside it is not a blur of the field.
            if (clicking.current || event.currentTarget.contains(event.relatedTarget)) return;
            setOpen(false);
            setActive(-1);
            onBlur?.();
          }}
        >
          <input
            id={id}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            required={required}
            autoComplete="off"
            placeholder={placeholder}
            value={value}
            className={cn(CONTROL, "border-line-strong")}
            onChange={(event) => {
              onChange(event.target.value);
              setOpen(true);
              setActive(-1);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                if (!open) {
                  setOpen(true);
                  return;
                }
                if (rows.length === 0) return;
                // Wraps at both ends. -1 is "nothing active yet", which ArrowDown turns into the
                // first row and ArrowUp into the last.
                setActive((current) =>
                  event.key === "ArrowDown"
                    ? current + 1 >= rows.length
                      ? 0
                      : current + 1
                    : current <= 0
                      ? rows.length - 1
                      : current - 1,
                );
                return;
              }
              if (event.key === "Enter" && open && active >= 0) {
                event.preventDefault();
                choose(active);
                return;
              }
              if (event.key === "Escape" && open) {
                // Closes the list and keeps what was typed. Escape must not undo an answer.
                event.preventDefault();
                setOpen(false);
                setActive(-1);
              }
            }}
          />

          {open && (
            <ul
              id={listId}
              role="listbox"
              aria-label={label}
              // Above the fields below it, and scrollable rather than tall — a directory of a few
              // hundred communities must not push the Register button off the screen.
              className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-line-strong bg-paper shadow-lg"
              onMouseDown={() => (clicking.current = true)}
              onMouseUp={() => (clicking.current = false)}
            >
              {rows.length === 0 ? (
                <li className="px-3 py-2 text-caption text-muted">
                  Nothing matches. Type it in — anything may be entered here.
                </li>
              ) : (
                rows.map((row, index) => (
                  <li key={`${row.listed ? "listed" : "typed"}-${row.text}`}>
                    <button
                      type="button"
                      id={`${listId}-${index}`}
                      role="option"
                      aria-selected={row.text === value}
                      tabIndex={-1}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => {
                        clicking.current = false;
                        choose(index);
                      }}
                      className={cn(
                        "flex min-h-touch w-full items-center px-3 text-left transition",
                        index === active ? "bg-accent-surface" : "hover:bg-sunk",
                        !row.listed && "border-t border-line",
                      )}
                    >
                      {row.listed ? (
                        row.text
                      ) : (
                        <span>
                          Use <span className="font-semibold">“{row.text}”</span>
                          <span className="text-muted"> — not in the list</span>
                        </span>
                      )}
                    </button>
                  </li>
                ))
              )}
              {hidden > 0 && (
                <li className="border-t border-line px-3 py-2 text-caption text-muted">
                  {hidden} more {hidden === 1 ? "match" : "matches"} — keep typing to narrow the
                  list.
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </FieldShell>
  );
}

/**
 * A large toggle, for choosing from a set of things.
 *
 * Used for category selection at the registration desk, where the grid is "large toggles grouped by
 * competition, showing live entry counts against any limit" (ux.md §3.3). Renders as a real
 * checkbox so it is keyboard- and screen-reader-operable without any of the usual div-with-role
 * pretending.
 */
export function ToggleTile({
  label,
  detail,
  checked,
  disabled,
  onChange,
  name,
}: {
  label: string;
  detail?: ReactNode | undefined;
  checked: boolean;
  disabled?: boolean | undefined;
  onChange: (checked: boolean) => void;
  name?: string | undefined;
}): ReactNode {
  return (
    <label
      className={cn(
        // `h-full` so a row of tiles is even: one tile carrying a line of detail must not leave the
        // plain tile beside it visibly short, and the box is the target — a ragged row of targets
        // is harder to hit as well as untidy.
        "flex h-full min-h-touch cursor-pointer items-start gap-3 rounded-lg border-2 p-3 transition",
        checked
          ? "border-brand bg-accent-surface shadow-sm"
          : "border-line bg-paper/90 hover:bg-white",
        disabled && "cursor-not-allowed opacity-55",
      )}
    >
      <input
        type="checkbox"
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1 size-5 accent-[var(--pw-brand-primary)]"
      />
      <span className="min-w-0">
        <span className="block font-semibold">{label}</span>
        {detail && <span className="block text-caption text-muted">{detail}</span>}
      </span>
    </label>
  );
}

/* ------------------------------------------------------------------ layout */

export function Stack({
  gap = 4,
  className,
  children,
}: {
  gap?: 1 | 2 | 3 | 4 | 6 | 8 | undefined;
  className?: string | undefined;
  children: ReactNode;
}): ReactNode {
  const gaps = { 1: "gap-1", 2: "gap-2", 3: "gap-3", 4: "gap-4", 6: "gap-6", 8: "gap-8" } as const;
  return <div className={cn("flex flex-col", gaps[gap], className)}>{children}</div>;
}

export function Cluster({
  className,
  children,
}: {
  className?: string | undefined;
  children: ReactNode;
}): ReactNode {
  return <div className={cn("flex flex-wrap items-center gap-gap", className)}>{children}</div>;
}

/**
 * Fields side by side, with their labels, help text and controls in line across the row.
 *
 * A grid of plain columns does not do this. Each field is as tall as its own content, so a field
 * with a line of help text sits its control lower than the field beside it, and the further down
 * the form you read the worse the drift gets — the labels stop looking like a row and start looking
 * like a mistake.
 *
 * `FieldShell` claims four rows — label, help, control, error — and this container is what those
 * rows belong to, so every field in a band shares them. A field with no help text leaves that row
 * empty rather than closing it up, and the row is only as tall as the tallest help text in it.
 *
 * `data-field-band` is what switches the fields into that subgrid; a plain `grid` parent must not,
 * because its row gap would land between a label and its own control rather than between fields.
 * Column gap only, for the same reason — the rows are the fields' to space.
 *
 * **Columns follow the space this band actually has, not the width of the window.** They used to be
 * `sm:grid-cols-3`, which asks the wrong question: a breakpoint knows how wide the *viewport* is,
 * and a band inside the form builder's left column — or any card sitting beside a preview pane — has
 * nothing like that much room. Three columns were duly rendered into 440 px, which is how *Depends
 * on* came to wrap onto two lines above a select too narrow to show the word inside it.
 *
 * `auto-fit` with a floor asks the right one. A track appears only where a full-width one would not
 * fit, so the same band is three columns on a wide screen, two in a side panel and one on a phone
 * without being told which it is in. `min(100%, …)` is what allows the single column: without it the
 * floor itself overflows a container narrower than the floor. `auto-fit` collapses the tracks it
 * cannot fill, so a band never gets more columns than it has fields, and `columns` becomes what it
 * always meant — how tight these particular fields may be packed.
 */
export function FieldGrid({
  columns = 2,
  className,
  children,
}: {
  columns?: 2 | 3 | 4 | undefined;
  className?: string | undefined;
  children: ReactNode;
}): ReactNode {
  // The narrowest a control of this kind stays usable. Wider for a two-up band, because two fields
  // in a row are usually the long ones — a name, a date, an email.
  const floor = { 2: "18rem", 3: "15rem", 4: "12rem" } as const;
  return (
    <div
      data-field-band=""
      className={cn("grid gap-x-4", className)}
      style={{ gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${floor[columns]}), 1fr))` }}
    >
      {children}
    </div>
  );
}

/**
 * What to say when there is nothing.
 *
 * An empty state always names the next action, because "no results" with no way forward is where
 * an untrained volunteer stops.
 */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: ReactNode | undefined;
  action?: ReactNode | undefined;
}): ReactNode {
  return (
    <Card className="border-dashed bg-white/70 py-12 text-center">
      <p className="text-body-large font-semibold">{title}</p>
      {description && <p className="mx-auto mt-1 max-w-[52ch] text-muted">{description}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </Card>
  );
}

/**
 * A placeholder while something loads.
 *
 * Shaped like the content it replaces so the page does not jump when it arrives. Never used to gate
 * an action — the arena surfaces write locally first and never show a spinner in front of a queue.
 */
export function Skeleton({ rows = 3 }: { rows?: number }): ReactNode {
  return (
    <div aria-hidden="true" className="space-y-2">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="h-touch animate-pulse rounded-md bg-sunk" />
      ))}
    </div>
  );
}

export function VisuallyHidden({ children }: { children: ReactNode }): ReactNode {
  return <span className="sr-only">{children}</span>;
}
